import { useState, useRef, useCallback, useEffect } from 'react'
import PropTypes from 'prop-types'

import * as d3 from 'd3'

import { pluralize } from '@aytm/app/stats/lib/util/helpers'
import { formatPoint, calcCoordAngle } from '@aytm/app/stats/page/data-centrifuge/lib/utils'
import { getLinkOpacity } from '@aytm/app/stats/page/data-centrifuge/lib/d3-helpers'
import { can, CAPABILITY } from '@aytm/app/stats/page/data-quality-report/lib/tiers'

import style from '@aytm/app/stats/page/data-centrifuge/styles/Tooltip.module.scss'

// Map the node's angle (clockwise from east) to one of the 8 corner directions. The box hangs
// inward (toward the cluster centre); the arrow sits on the outward edge, offset to the corner
// nearest the node — never centred.
export const getPositionClass = angle => {
  if (angle >= 315 || angle < 45) return angle >= 315 ? 'righttop' : 'rightbottom'
  if (angle < 135) return angle < 90 ? 'bottomright' : 'bottomleft'
  if (angle < 225) return angle < 180 ? 'leftbottom' : 'lefttop'
  return angle < 270 ? 'topleft' : 'topright'
}

/**
 * Tooltip component rendered inline
 */
export const Tooltip = ({ tier, reviewEvidence, position, positionClass, scoreData, visible }) => {
  // Early return BEFORE any hook — the previous `useMemo` below the return
  // violated the Rules of Hooks (hook count changed on visible flip → React
  // threw "Rendered fewer hooks than during the previous render").
  // The popover stays mounted through the dismissal fade — the hold that follows clears `scoreData`.
  if (!scoreData) return null

  // Tooltips typically list ≤3 vectors; the per-render sort is cheap enough
  // that memoizing it is not worth a hook slot. Sort descending by weight;
  // break ties with `i` ascending.
  const vectors = (scoreData.vectors || []).slice().sort((a, b) => b.weight - a.i / 1000 - (a.weight - b.i / 1000))

  const handleReviewResponse = () => {
    const reviewResponseEvent = new CustomEvent('reviewResponse', { detail: { uid: scoreData.id } })
    dispatchEvent(reviewResponseEvent)
  }

  return (
    <div
      data-testid="stat-dc-tooltip"
      className={`${style.tooltip} ${style['tooltip-' + positionClass]}`}
      style={{
        top: position.top,
        left: position.left,
        opacity: visible ? 0.95 : 0,
        pointerEvents: visible ? 'auto' : 'none'
      }}
    >
      {/* Reuse the shared popup box + arrow so the tooltip matches the rfd-ui Popup component.
          positionClass (righttop/topleft/…) maps 1:1 to the rfd-ui-popup-arrow-* variants. */}
      <div className={`${style['tooltip-box']} rfd-ui-popup`}>
        <div className={`rfd-ui-popup-arrow rfd-ui-popup-arrow-${positionClass}`} />
        <div className={style['tooltip-content']}>
          {can(tier, CAPABILITY.RELEASE_QUARANTINE) ? (
            <p data-testid="stat-dc-tooltip-id" className={style['tooltip-id']}>
              Response ID: <span>{scoreData.id}</span>
            </p>
          ) : null}
          {0 < vectors.length ? (
            <div className={style['tooltip-vectors']}>
              <p className={style['tooltip-vectors-title']}>
                {pluralize(vectors.length, 'Triggered vector:', 'Triggered vectors:')}
              </p>
              <ul className={style['tooltip-vector-list']}>
                {vectors.map(v => (
                  <li key={v.id}>{v.text}</li>
                ))}
              </ul>
            </div>
          ) : null}
          {/* Evidence review needs the tier and the producer's own grant. */}
          {reviewEvidence ? (
            <span data-testid="stat-dc-tooltip-review" className={style['tooltip-link']} onClick={handleReviewResponse}>
              Review Evidence
            </span>
          ) : null}
        </div>
      </div>
    </div>
  )
}

Tooltip.propTypes = {
  tier: PropTypes.string,
  reviewEvidence: PropTypes.bool,
  position: PropTypes.shape({ top: PropTypes.number, left: PropTypes.number }),
  positionClass: PropTypes.string,
  scoreData: PropTypes.object,
  visible: PropTypes.bool
}

/**
 * Custom hook for tooltip management
 */
export const useTooltip = ({ svgRef, tier, reviewEvidence, data, linkOpacity, selectedVector }) => {
  const [tooltipState, setTooltipState] = useState({
    position: { top: 0, left: 0 },
    positionClass: 'top',
    scoreData: null,
    visible: false
  })

  const currentNodeRef = useRef(null)
  const currentDataRef = useRef(null)

  const tooltipTimerRef = useRef(null)
  const selectedRespondentIdRef = useRef(null)
  // Holds the dismiss function for the currently-active tooltip:
  // invoking it removes the document listener AND restores the dot's index position.
  // Used to (a) dismiss prior tooltip before showing a new one (fixes listener accumulation)
  // and (b) clean up on unmount (fixes listener leak).
  const activeDismissRef = useRef(null)

  // Move score back to its original index position.
  // The function is recomputed whenever its deps change (data, linkOpacity,
  // selectedVector). Callers that need to invoke the *latest* version from a
  // long-lived closure (see `activeDismissRef` below) must read through
  // `moveScoreRef` instead of capturing the callback directly — otherwise a
  // tooltip dismissed after a selection change would restore link opacities
  // from stale state.
  const moveScoreToIndex = useCallback(
    (node, d) => {
      if (!svgRef?.current) return

      const nodes = []
      svgRef.current.selectAll('.group-score').each(function () {
        nodes.push(this)
      })

      node.parentNode.insertBefore(node, nodes[d3.select(node).attr('index')])

      for (const id of d.links) {
        const linkNodes = []
        svgRef.current.selectAll('.group-link').each(function () {
          linkNodes.push(this)
        })

        const link = svgRef.current.select(`#group-link-${id}`)
        link.style('opacity', _d => getLinkOpacity(_d, data, linkOpacity, selectedVector))
        link.node().parentNode.insertBefore(link.node(), linkNodes[link.attr('index')])
      }
    },
    [svgRef, data, linkOpacity, selectedVector]
  )

  // Keep a ref to the latest moveScoreToIndex so dismiss-handle closures
  // registered at show time always call the current version.
  const moveScoreRef = useRef(moveScoreToIndex)
  useEffect(() => {
    moveScoreRef.current = moveScoreToIndex
  }, [moveScoreToIndex])

  // Move score to front (for highlighting)
  const moveScoreToFront = useCallback(
    (node, d) => {
      if (!svgRef?.current) return

      node.parentNode.appendChild(node)

      for (const id of d.links) {
        const link = svgRef.current.select(`#group-link-${id}`)
        link.style('opacity', 1)
        link.node().parentNode.appendChild(link.node())
      }
    },
    [svgRef]
  )

  // Hide tooltip
  const hideTooltip = useCallback(() => {
    selectedRespondentIdRef.current = null

    setTooltipState(prev => ({ ...prev, visible: false }))

    tooltipTimerRef.current = setTimeout(() => {
      tooltipTimerRef.current = null

      setTooltipState({
        position: { top: 0, left: 0 },
        positionClass: 'top',
        scoreData: null,
        visible: false
      })
    }, 200)
  }, [])

  // Show tooltip
  const showTooltip = useCallback(
    (node, d) => {
      // Dismiss previous tooltip's listener + restore its dot position before
      // registering a new one. Prevents listener accumulation across rapid
      // dot-to-dot clicks and ensures the previous dot is not left at the front.
      if (activeDismissRef.current) {
        activeDismissRef.current()
        activeDismissRef.current = null
      }

      if (tooltipTimerRef.current) {
        clearTimeout(tooltipTimerRef.current)
        tooltipTimerRef.current = null
      }

      if (!data) return

      currentDataRef.current = d
      currentNodeRef.current = node

      const p = formatPoint(d, data.angle)
      let top = data.center.y + p.y + 2
      let left = data.center.x + p.x + 2

      const angle = calcCoordAngle({ x: p.x, y: p.y })
      const positionClass = getPositionClass(angle)

      // Nudge the anchor point to the node's edge in the direction the box hangs.
      if (positionClass.startsWith('right')) left -= d.r
      else if (positionClass.startsWith('left')) left += d.r
      else if (positionClass.startsWith('bottom')) top -= d.r
      else top += d.r

      setTooltipState({
        position: { top, left },
        positionClass,
        scoreData: d,
        visible: true
      })

      // Handle outside click to close tooltip
      const currentSelectedId = selectedRespondentIdRef.current
      const handleOutsideClick = event => {
        if (event.target == node || event.target.parentNode == node || event.target.parentNode?.parentNode == node)
          return

        // Check if click is inside tooltip
        const tooltipEl = document.querySelector(`.${style.tooltip}`)
        if (tooltipEl?.contains(event.target)) return

        activeDismissRef.current = null
        removeDismissListeners()
        moveScoreRef.current(node, d)

        if (currentSelectedId != null && currentSelectedId != selectedRespondentIdRef.current) return
        hideTooltip()
      }

      // The tooltip is positioned against the chart container, so it travels with its node and
      // has to go once the page moves under it.
      const handleScrollDismiss = () => {
        activeDismissRef.current = null
        removeDismissListeners()
        moveScoreRef.current(node, d)
        hideTooltip()
      }

      const removeDismissListeners = () => {
        document.removeEventListener('mousedown', handleOutsideClick)
        document.removeEventListener('scroll', handleScrollDismiss, true)
      }

      document.addEventListener('mousedown', handleOutsideClick)
      // Capture: a scroll event does not bubble, and the surface scrolls in its own container
      // rather than the document.
      document.addEventListener('scroll', handleScrollDismiss, true)

      // Record the dismiss hook so the next showTooltip or cleanupTooltip can tear this down.
      // We go through `moveScoreRef.current` (not a captured `moveScoreToIndex`)
      // so that a selection change between show and dismiss repaints link
      // opacities from the latest state instead of stale closure state.
      activeDismissRef.current = () => {
        removeDismissListeners()
        moveScoreRef.current(node, d)
      }
    },
    [data, hideTooltip]
  )

  // Handle score click
  const handleScoreClick = useCallback(
    (node, d) => {
      if (selectedRespondentIdRef.current == d.id) return

      // Below the evidence-review tier the ID and the review link are gated out, so the
      // vectors list is the tooltip's only content — skip opening an empty box when it has none.
      if (!d.vectors?.length && !reviewEvidence) return

      selectedRespondentIdRef.current = d.id

      moveScoreToFront(node, d)
      showTooltip(node, d)
    },
    [tier, reviewEvidence, moveScoreToFront, showTooltip]
  )

  // Render tooltip inline
  const renderTooltip = useCallback(
    () => (
      <Tooltip
        tier={tier}
        reviewEvidence={reviewEvidence}
        position={tooltipState.position}
        positionClass={tooltipState.positionClass}
        scoreData={tooltipState.scoreData}
        visible={tooltipState.visible}
      />
    ),
    [tooltipState, tier, reviewEvidence]
  )

  // Cleanup
  const cleanupTooltip = useCallback(() => {
    if (tooltipTimerRef.current) {
      clearTimeout(tooltipTimerRef.current)
      tooltipTimerRef.current = null
    }
    // Remove any still-attached document listener and restore the dot's index position.
    // Without this, a component that unmounts with a visible tooltip would leak the
    // listener plus closure references to the old DOM nodes.
    if (activeDismissRef.current) {
      activeDismissRef.current()
      activeDismissRef.current = null
    }
  }, [])

  return {
    handleScoreClick,
    renderTooltip,
    cleanupTooltip
  }
}

export default useTooltip
